import React, { useEffect, useState } from 'react';
import Navbar from '../components/profile-components/Navbar';
import Topbar from '../components/profile-components/Topbar';
import AddressVerificationFields from '../components/common/AddressVerificationFields';
import './SalesPages.css';

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || 'https://agent-book-crm-backend.onrender.com';

function NewLead() {
  const [statuses, setStatuses] = useState([]);
  const [sources, setSources] = useState([]);
  const [users, setUsers] = useState([]);
  const [aorUserId, setAorUserId] = useState('');
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    lead_status_id: '',
    lead_source_id: '',
    referred_by: '',
    first_name: '',
    last_name: '',
    marital_status: '',
    spouse_name: '',
    dob: '',
    gender: '',
    medicaid_number: '',
    medicare_number: '',
    part_a_date: '',
    part_b_date: '',
    phone: '',
    email: '',
    street_address: '',
    unit: '',
    city: '',
    state: '',
    zip_code: '',
    county: '',
    address_verified: 0,
  });

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      console.error('No token found – user is not logged in.');
      return;
    }

    const headers = {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    };

    fetch(`${API_BASE_URL}/api/lead-statuses`, { headers })
      .then((res) => res.json())
      .then((data) => setStatuses(Array.isArray(data) ? data : []))
      .catch((err) => console.error('Status load error:', err));

    fetch(`${API_BASE_URL}/api/lead-sources`, { headers })
      .then((res) => res.json())
      .then((data) => setSources(Array.isArray(data) ? data : []))
      .catch((err) => console.error('Source load error:', err));

    fetch(`${API_BASE_URL}/api/users`, { headers })
      .then((res) => res.json())
      .then((data) => setUsers(Array.isArray(data) ? data : []))
      .catch((err) => console.error('Users load error:', err));

    fetch(`${API_BASE_URL}/api/users/profile`, { headers })
      .then((res) => res.json())
      .then((profile) => {
        if (profile?.id) {
          setAorUserId(String(profile.id));
        }
      })
      .catch((err) => console.error('Profile load error:', err));
  }, []);

  const normalizeDate = (dateStr) => {
    const d = new Date(dateStr);
    return isNaN(d.getTime()) ? '' : d.toISOString().split('T')[0];
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleAddressChange = (fieldName, value) => {
    setFormData((prev) => ({
      ...prev,
      [fieldName]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const payload = {
      ...formData,
      aor_user_id: aorUserId,
      dob: normalizeDate(formData.dob),
      part_a_date: normalizeDate(formData.part_a_date),
      part_b_date: normalizeDate(formData.part_b_date),
      address_verified: Number(formData.address_verified) ? 1 : 0,
    };

    try {
      setSaving(true);

      const res = await fetch(`${API_BASE_URL}/api/leads`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) throw new Error('Failed to save lead');

      alert('✅ Lead saved successfully!');
      window.location.href = '/leads';
    } catch (err) {
      console.error('Submit error:', err);
      alert('Something went wrong!');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Navbar />
      <Topbar />

      <div className="main-content sales-page">
        <div className="sales-hero sales-profile-hero">
          <div>
            <p className="sales-eyebrow">Agent Book CRM</p>
            <h1 className="sales-title">Create New Lead</h1>
            <p className="sales-subtitle">
              Add a new lead with ownership, personal details, Medicare fields, and contact information.
            </p>
          </div>

          <div className="sales-header-actions">
            <button
              type="submit"
              form="new-lead-form"
              className="sales-primary-btn"
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save Lead'}
            </button>
          </div>
        </div>

        <form id="new-lead-form" onSubmit={handleSubmit}>
          <div className="sales-profile-grid">
            <div className="sales-section-card sales-section-lead">
              <div className="sales-section-head">
                <h2>Lead Information</h2>
                <p>Status, source, ownership, personal details, and Medicare fields.</p>
              </div>

              <div className="sales-form-fields">
                <div className="sales-field">
                  <label>Status</label>
                  <select
                    name="lead_status_id"
                    value={formData.lead_status_id}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select</option>
                    {statuses.map((status) => (
                      <option key={status.id} value={status.id}>
                        {status.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="sales-field">
                  <label>Source</label>
                  <select
                    name="lead_source_id"
                    value={formData.lead_source_id}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select</option>
                    {sources.map((source) => (
                      <option key={source.id} value={source.id}>
                        {source.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="sales-field">
                  <label>Referred By</label>
                  <input
                    name="referred_by"
                    value={formData.referred_by}
                    onChange={handleChange}
                  />
                </div>

                <div className="sales-field">
                  <label>AOR</label>
                  <select
                    name="aor_user_id"
                    value={aorUserId}
                    onChange={(e) => setAorUserId(e.target.value)}
                  >
                    <option value="">Select</option>
                    {users.map((user) => (
                      <option key={user.id} value={user.id}>
                        {user.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="sales-field">
                  <label>First Name</label>
                  <input
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="sales-field">
                  <label>Last Name</label>
                  <input
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="sales-field">
                  <label>Marital Status</label>
                  <select
                    name="marital_status"
                    value={formData.marital_status}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select</option>
                    <option value="Single">Single</option>
                    <option value="Married">Married</option>
                    <option value="Separated">Separated</option>
                    <option value="Widowed">Widowed</option>
                    <option value="Divorced">Divorced</option>
                  </select>
                </div>

                <div className="sales-field">
                  <label>Spouse Name</label>
                  <input
                    name="spouse_name"
                    value={formData.spouse_name}
                    onChange={handleChange}
                  />
                </div>

                <div className="sales-field">
                  <label>Date of Birth</label>
                  <input
                    name="dob"
                    type="date"
                    value={formData.dob}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="sales-field">
                  <label>Gender</label>
                  <select
                    name="gender"
                    value={formData.gender}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                <div className="sales-field">
                  <label>Medicaid Number</label>
                  <input
                    name="medicaid_number"
                    value={formData.medicaid_number}
                    onChange={handleChange}
                  />
                </div>

                <div className="sales-field">
                  <label>Medicare Number</label>
                  <input
                    name="medicare_number"
                    value={formData.medicare_number}
                    onChange={handleChange}
                  />
                </div>

                <div className="sales-field">
                  <label>Part A Date</label>
                  <input
                    name="part_a_date"
                    type="date"
                    value={formData.part_a_date}
                    onChange={handleChange}
                  />
                </div>

                <div className="sales-field">
                  <label>Part B Date</label>
                  <input
                    name="part_b_date"
                    type="date"
                    value={formData.part_b_date}
                    onChange={handleChange}
                  />
                </div>
              </div>
            </div>

            <div className="sales-section-card sales-section-subscription">
              <div className="sales-section-head">
                <h2>Contact Details</h2>
                <p>Address and contact information for this lead.</p>
              </div>

              <div className="sales-form-fields">
                <div className="sales-field">
                  <label>Phone</label>
                  <input
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="sales-field">
                  <label>Email</label>
                  <input
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                </div>

                <AddressVerificationFields
                  values={formData}
                  onChange={handleAddressChange}
                  fieldMap={{
                    street: 'street_address',
                    unit: 'unit',
                    city: 'city',
                    state: 'state',
                    zip: 'zip_code',
                    county: 'county',
                    addressVerified: 'address_verified',
                  }}
                />

                <input
                  type="hidden"
                  name="address_verified"
                  value={formData.address_verified}
                />
              </div>
            </div>
          </div>
        </form>
      </div>
    </>
  );
}

export default NewLead;